import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry,
  Position,
} from 'geojson';
import { loadDuckDB, registerFile } from './TrackImporterDuckDB';
import { parseCsv, parseTsv } from './track-parsers/csv-parser';
import type { TrackPoint } from './track-parsers/track-types';

const MAX_FEATURES = 10_000;
const INTERNAL_COLUMNS = new Set(['__geometry_json', '__geometry_type', '__is_valid']);

export interface SpatialGeometryCount {
  type: string;
  count: number;
}

export interface SpatialSummary {
  featureCount: number;
  validCount: number;
  invalidCount: number;
  vertexCount: number;
  bounds: [number, number, number, number] | null;
  geometryCounts: SpatialGeometryCount[];
}

export interface SpatialAnalysisResult extends SpatialSummary {
  fileName: string;
  geojson: FeatureCollection;
  truncated: boolean;
  engine: 'duckdb-spatial' | 'browser-fallback';
  warning?: string;
}

interface DuckDbRow {
  __geometry_json?: unknown;
  __geometry_type?: unknown;
  __is_valid?: unknown;
  [key: string]: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isGeometry(value: unknown): value is Geometry {
  if (!isRecord(value) || typeof value.type !== 'string') return false;
  if (value.type === 'GeometryCollection') return Array.isArray(value.geometries);
  return Array.isArray(value.coordinates);
}

function serialisableValue(value: unknown): unknown {
  if (typeof value === 'bigint') {
    return Number.isSafeInteger(Number(value)) ? Number(value) : value.toString();
  }
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map(serialisableValue);
  if (isRecord(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, nested]) => [key, serialisableValue(nested)]),
    );
  }
  return value;
}

function featureProperties(row: DuckDbRow): GeoJsonProperties {
  const properties: GeoJsonProperties = {};
  for (const [key, value] of Object.entries(row)) {
    if (key === 'geom' || INTERNAL_COLUMNS.has(key) || value === undefined) continue;
    properties[key] = serialisableValue(value);
  }
  return properties;
}

function rowToFeature(row: DuckDbRow): Feature<Geometry> | null {
  if (typeof row.__geometry_json !== 'string') return null;
  const geometry = JSON.parse(row.__geometry_json) as unknown;
  if (!isGeometry(geometry)) return null;
  return {
    type: 'Feature',
    geometry,
    properties: featureProperties(row),
  };
}

function visitPositions(value: unknown, visit: (position: Position) => void): void {
  if (!Array.isArray(value) || value.length === 0) return;
  if (typeof value[0] === 'number' && typeof value[1] === 'number') {
    visit(value as Position);
    return;
  }
  for (const nested of value) visitPositions(nested, visit);
}

function visitGeometryPositions(geometry: Geometry, visit: (position: Position) => void): void {
  if (geometry.type === 'GeometryCollection') {
    for (const nested of geometry.geometries) visitGeometryPositions(nested, visit);
    return;
  }
  visitPositions(geometry.coordinates, visit);
}

export function summariseFeatureCollection(
  geojson: FeatureCollection,
  validities: Array<boolean | undefined> = [],
): SpatialSummary {
  const geometryCounts = new Map<string, number>();
  let validCount = 0;
  let invalidCount = 0;
  let vertexCount = 0;
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;

  geojson.features.forEach((feature, index) => {
    if (!feature.geometry) {
      invalidCount += 1;
      return;
    }

    const validity = validities[index];
    if (validity === false) invalidCount += 1;
    else validCount += 1;

    const type = feature.geometry.type;
    geometryCounts.set(type, (geometryCounts.get(type) ?? 0) + 1);
    visitGeometryPositions(feature.geometry, (position) => {
      const [longitude, latitude] = position;
      if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return;
      vertexCount += 1;
      west = Math.min(west, longitude);
      south = Math.min(south, latitude);
      east = Math.max(east, longitude);
      north = Math.max(north, latitude);
    });
  });

  return {
    featureCount: geojson.features.length,
    validCount,
    invalidCount,
    vertexCount,
    bounds: Number.isFinite(west) ? [west, south, east, north] : null,
    geometryCounts: Array.from(geometryCounts, ([type, count]) => ({ type, count }))
      .sort((a, b) => a.type.localeCompare(b.type)),
  };
}

export function normaliseGeoJson(content: string): FeatureCollection {
  const root = JSON.parse(content) as unknown;
  if (!isRecord(root) || typeof root.type !== 'string') {
    throw new Error('GeoJSON root must be a FeatureCollection, Feature, or geometry.');
  }

  if (root.type === 'FeatureCollection') {
    if (!Array.isArray(root.features)) throw new Error('GeoJSON FeatureCollection is missing features.');
    return root as unknown as FeatureCollection;
  }

  if (root.type === 'Feature') {
    return { type: 'FeatureCollection', features: [root as unknown as Feature] };
  }

  if (isGeometry(root)) {
    return {
      type: 'FeatureCollection',
      features: [{ type: 'Feature', geometry: root, properties: {} }],
    };
  }

  throw new Error(`Unsupported GeoJSON type: ${root.type}`);
}

function trackPointsToGeoJson(points: TrackPoint[]): FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: points.map((point) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [point.longitude, point.latitude] },
      properties: {
        ...(point.altitudeFt !== undefined ? { altitudeFt: point.altitudeFt } : {}),
        ...(point.groundSpeedKts !== undefined ? { groundSpeedKts: point.groundSpeedKts } : {}),
        ...(point.heading !== undefined ? { heading: point.heading } : {}),
        ...(point.timestamp !== undefined ? { timestamp: point.timestamp } : {}),
      },
    })),
  };
}

export function normaliseDelimitedText(content: string, delimiter: ',' | '\t'): FeatureCollection {
  const parsed = delimiter === '\t' ? parseTsv(content) : parseCsv(content);
  return trackPointsToGeoJson(parsed.points);
}

function fileExtension(fileName: string): string {
  return fileName.toLowerCase().split('.').pop() ?? '';
}

async function fallbackFeatureCollection(file: File): Promise<FeatureCollection | null> {
  const extension = fileExtension(file.name);
  if (extension === 'geojson' || extension === 'json') return normaliseGeoJson(await file.text());
  if (extension === 'csv') return normaliseDelimitedText(await file.text(), ',');
  if (extension === 'tsv') return normaliseDelimitedText(await file.text(), '\t');
  return null;
}

async function sourceFileForDuckDb(file: File): Promise<{ file: File; fallback: FeatureCollection | null }> {
  const fallback = await fallbackFeatureCollection(file);
  const extension = fileExtension(file.name);
  if (extension !== 'csv' && extension !== 'tsv') return { file, fallback };

  return {
    file: new File(
      [JSON.stringify(fallback)],
      `${file.name.replace(/\.[^.]+$/, '')}.geojson`,
      { type: 'application/geo+json' },
    ),
    fallback,
  };
}

function userFacingError(file: File, error: unknown): Error {
  const detail = error instanceof Error ? error.message : String(error);
  return new Error(`DuckDB Spatial could not read ${file.name}. ${detail}`);
}

export async function analyzeSpatialFile(file: File): Promise<SpatialAnalysisResult> {
  const extension = fileExtension(file.name);
  if (!['geojson', 'json', 'kml', 'gpx', 'csv', 'tsv'].includes(extension)) {
    throw new Error('Choose a GeoJSON, KML, GPX, CSV, or TSV file.');
  }

  const source = await sourceFileForDuckDb(file);
  try {
    const { db, conn } = await loadDuckDB();
    const vpath = `/spatial-analysis-${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExtension(source.file.name)}`;
    await registerFile(db, source.file, vpath);
    try {
      const result = await conn.query(`
        SELECT
          * EXCLUDE (geom),
          CAST(ST_AsGeoJSON(geom) AS VARCHAR) AS __geometry_json,
          CAST(ST_GeometryType(geom) AS VARCHAR) AS __geometry_type,
          ST_IsValid(geom) AS __is_valid
        FROM ST_Read('${vpath}')
        WHERE geom IS NOT NULL
        LIMIT ${MAX_FEATURES + 1};
      `);
      const rows = result.toArray().map((row: unknown) => {
        if (isRecord(row) && typeof row.toJSON === 'function') return row.toJSON();
        return row;
      }) as DuckDbRow[];
      const truncated = rows.length > MAX_FEATURES;
      const includedRows = rows.slice(0, MAX_FEATURES);
      const features: Feature<Geometry>[] = [];
      const validities: Array<boolean | undefined> = [];
      for (const row of includedRows) {
        const feature = rowToFeature(row);
        if (!feature) continue;
        features.push(feature);
        validities.push(typeof row.__is_valid === 'boolean' ? row.__is_valid : undefined);
      }
      const geojson: FeatureCollection = { type: 'FeatureCollection', features };
      return {
        fileName: file.name,
        geojson,
        truncated,
        engine: 'duckdb-spatial',
        ...summariseFeatureCollection(geojson, validities),
      };
    } finally {
      try { await db.dropFile(vpath); } catch { /* best effort */ }
    }
  } catch (error) {
    if (!source.fallback) throw userFacingError(file, error);
    return {
      fileName: file.name,
      geojson: source.fallback,
      truncated: false,
      engine: 'browser-fallback',
      warning: 'DuckDB Spatial was unavailable, so the browser parser was used for this file.',
      ...summariseFeatureCollection(source.fallback),
    };
  }
}
